import { rulesFor } from '@full-elevator/content';
import type { BuildingRules, Elevator, GameState, LevelDef, Passenger } from '@full-elevator/sim';
import {
  alightFloor,
  canBoard,
  canTakeStairs,
  capacityOf,
  carsOf,
  costOfLoss,
  currentFloor,
  deadlineFraction,
  deadlineRemaining,
  inArrivalOrder,
  isParked,
  lossesToSpare,
  MAX_STOPS,
  MILLI_PER_FLOOR,
  nextStops,
  patienceRemaining,
  planOf,
  queueableWith,
  valueOfDelivery,
} from '@full-elevator/sim';
import type { Application } from 'pixi.js';
import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type { CoachStep, Spot } from '../coach.js';
import { floorName, groundOf } from '../floorNames.js';
import type { Session } from '../session.js';
import { COLORS, DEPARTURES, DISPLAY_FONT, floorColor, LAYOUT, LIFT_PARTS } from '../theme.js';
import { clockFor } from '../ui/clock.js';
import type { Atmosphere, FacadeStrip } from './atmosphere.js';
import { atmosphereOf, EARTH, FACADE_EDGE, paintEarth, paintPavement } from './atmosphere.js';
import { CABIN_GAP, cabinSize, readableStops, stopsOwed } from './cabinLayout.js';
import type { Box } from './coachOverlay.js';
import { CoachOverlay } from './coachOverlay.js';
import {
  paintBeacon,
  paintReadyRing,
  paintSiren,
  sirenPair,
  sirenPhase,
  underWay,
} from './emergency.js';
import {
  blinkAlpha,
  cheerFor,
  Feedback,
  inLastSeconds,
  paintWarning,
  runningOut,
  STOP_WINDOW_MS,
  streakCheer,
} from './feedback.js';
import { paintEyes, paintFigure, paintLeg, paintLids, paintSweat } from './figure.js';
import { roomForFloor, rugsOn } from './floorRooms.js';
import type { LiftLook } from './liftParts.js';
import {
  liftLookOf,
  paintCabinTrim,
  paintDoorPanel,
  paintHoist,
  paintMotor,
  paintStreaks,
} from './liftParts.js';
import type { Exit } from './motion.js';
import {
  approach,
  blinking,
  EXIT_MS,
  exitPose,
  GAZE_UNITS,
  gaze,
  idleOffset,
  legSwing,
  seedOf,
  toeLift,
  WALK_SPEED,
  walkBob,
} from './motion.js';
import type { Build, Look } from './people.js';
import { buildFor, crewOf, headTop, lookOf, ticketTop, unit } from './people.js';
import type { RoomFrame } from './roomLife.js';
import { RoomLife } from './roomLife.js';
import { roomTexture } from './roomTextures.js';
import { departures, paintBoard } from './trains.js';

/**
 * What the chip says.
 *
 * Normally the floor this person wants. In a building with an interchange it is
 * where they get out *next*, with their real destination after it — because the
 * number on the chip is the number the player drives to, and a chip promising a
 * floor the lift will not stop at is a lie the game tells the player. The bots
 * had exactly this bug before `alightFloor` existed, and wedged.
 */
function stopLabel(passenger: Passenger, nextStop: number, ground: number): string {
  // A postman's round, then where he gets off (ADR-0031): every floor the car
  // owes him, in the order he gave them.
  if (passenger.errands.length > 0) {
    return [...passenger.errands, passenger.destination].map((f) => floorName(f, ground)).join('·');
  }
  // Named from the street (ADR-0028): the Transit Centre's platform is −2.
  return nextStop === passenger.destination
    ? floorName(passenger.destination, ground)
    : `${floorName(nextStop, ground)}›${floorName(passenger.destination, ground)}`;
}

/**
 * Draws the building.
 *
 * Reads simulation state and emits taps. It holds no rules of its own — if this
 * file ever needs to decide whether something is allowed, that decision belongs
 * in @full-elevator/sim instead.
 */

/** One leg of a whole person: it turns about the hip, its shoe taps on its own. */
interface Leg {
  readonly root: Container;
  readonly limb: Graphics;
  readonly shoe: Graphics;
}

/** How far a tapping foot lifts, in the figure's units. */
const TOE_UNITS = 2.4;

interface Chip {
  readonly root: Container;
  /**
   * The tap target: a transparent rectangle with real drawn geometry.
   *
   * A Container carrying only a `hitArea` did not reliably win hit testing here,
   * which is why select mode looked dead — taps fell through to the floor behind
   * the passenger. The floor targets have always been Graphics with actual
   * geometry and have always worked, so chips use the same shape of solution.
   */
  readonly hit: Graphics;
  /** The person holding the ticket, behind it (ADR-0018). Empty for a bare ticket. */
  readonly figure: Graphics;
  /** Their hands, gripping the ticket's edges — in front of it. */
  readonly hands: Graphics;
  readonly plate: Graphics;
  /** Redrawn every frame: patience and deadline are clocks, not decoration. */
  readonly meter: Graphics;
  readonly label: Text;
  lastKey: string;
  /** As painted — a transfer or luggage chip is wider, and the meters span it. */
  width: number;
  /** As painted: how much of a person this floor has room for; null for a bare ticket. */
  build: Build | null;
  readonly look: Look;
  /** Closed eyes, shown for a blink. */
  readonly lids: Graphics;
  /** Open eyes, on their own so they can look about. */
  readonly eyes: Graphics;
  /** One head unit in px: how far the eyes may turn. 0 when there are none. */
  eyeUnit: number;
  /** Where they are looking now, -1 (toward the lift) to 1, turning smoothly. */
  gaze: number;
  readonly legs: readonly [Leg, Leg];
  /** Where the person is along the floor right now, walking toward their place. */
  x: number;
  /** On screen last frame: a person who reappears walks in rather than appearing. */
  shown: boolean;
  /** When they came into view, on the view's own clock, for the fade-in. */
  bornMs: number;
  readonly seed: number;
  /** Leaving the floor, if they are: how, since when, and from where to where. */
  exit: { kind: Exit; startMs: number; from: number; to: number; y: number } | null;
}

/** A luggage traveller occupies two slots, so they are drawn twice as wide. */
function chipWidth(passenger: Passenger, size: number, nextStop = passenger.destination): number {
  // A transfer chip carries two numbers and a separator, so it needs the room a
  // luggage chip gets — otherwise the label spills over its own plate.
  const wide = passenger.size > 1 || nextStop !== passenger.destination;
  // A round carries several numbers beside the second seat's mark (ADR-0031).
  if (passenger.errands.length > 0) return size * (1.5 + 0.45 * passenger.errands.length);
  return wide ? size * 1.7 : size;
}

/**
 * How long a player may stall before the game points at something, and how long
 * it keeps flashing once it does.
 *
 * The flash is the attention-grab and it is finite on purpose — a blink that
 * never stops reads as an error state rather than a nudge. After a few pulses it
 * settles into a steady outline, which still says "these are your options"
 * without demanding anything. Any action resets the whole thing.
 */
/** Room for "+14" at chip height. */
const CROWD_PILL_WIDTH = 38;

/**
 * One ticket per stop the cabin still owes, with its riders stacked behind
 * (v0.8): the cabin is read by stop, and a ticket per stop stays readable on a
 * 12-floor level where a ticket per rider shrank to 6px.
 */
interface StopChip {
  readonly root: Container;
  readonly plate: Graphics;
  /** The most urgent courier deadline among this stop's riders: a clock, redrawn each frame. */
  readonly meter: Graphics;
  readonly label: Text;
  key: string;
  width: number;
  height: number;
}

/** What one car is drawn with. A bank of cars (ADR-0034) has one per car. */
interface CarRig {
  readonly cabin: Graphics;
  /** Its stop tickets and "+N", over the cabin. */
  readonly layer: Container;
  /** Its seat count, or FULL. */
  readonly label: Text;
  /**
   * Its doors, over the riders' tickets: they close on whoever boarded, and
   * see-through while anyone rides, so the numbers still read (the owner,
   * PLAYTEST_LOG 2026-10-30).
   */
  readonly doors: Graphics;
  readonly stopChips: Map<number, StopChip>;
  moreStops: { root: Container; plate: Graphics; label: Text } | null;
  /** The car and its seat count as drawn this frame, for a lesson to light. */
  box: Box | null;
  /** Just the car: a tap on it chooses it, a tap beside it in its shaft sends it. */
  body: Box | null;
}

/** "+N": the people on a floor who did not fit across the screen. */
interface CrowdPill {
  readonly root: Container;
  readonly bg: Graphics;
  readonly label: Text;
}

/**
 * The doors over a car with riders in it: enough to see them shut on the
 * riders, faint enough that every ticket's number still reads through them.
 */
const RIDDEN_DOOR_ALPHA = 0.3;

/** The rooms are faded: the tickets stay the loudest thing on screen. */
const ROOM_ALPHA = 0.72;

const HINT_AFTER_MS = 5000;
/** How long someone just introduced stays pointed out (ADR-0036). */
const POINT_MS = 3500;
const HINT_PULSE_MS = 900;
const HINT_PULSES = 3;

const CHIP_FONT = {
  fontFamily: DISPLAY_FONT,
  fontWeight: '700' as const,
};

/** Corner radius scales with the chip, so a cabin-sized chip stays the same shape. */
function chipRadius(size: number): number {
  return Math.round(size * 0.32);
}

/** The floor a car's doors are open or opening at, or null. Gates passenger tapping. */
function servicingAt(state: GameState, car: Elevator): number | null {
  if (car.phase !== 'servicing') return null;
  if (car.doorPhase !== 'open' && car.doorPhase !== 'opening') return null;
  return currentFloor(state, car);
}

/**
 * Which car stands open at each floor (every car's, ADR-0034), seen as if it
 * were the only one: a group blocked from it is blocked by its seats.
 */
function openDoors(state: GameState): Map<number, GameState> {
  const open = new Map<number, GameState>();
  for (const car of carsOf(state)) {
    const floor = servicingAt(state, car);
    if (floor !== null && !open.has(floor)) {
      open.set(floor, car === state.elevator ? state : { ...state, elevator: car });
    }
  }
  return open;
}

/** Patience by thirds-ish: fine, getting low, nearly gone. */
function patienceColour(fraction: number): number {
  if (fraction > 0.5) return COLORS.patienceOk;
  return fraction > 0.25 ? COLORS.patienceLow : COLORS.patienceOut;
}

export class ShaftView {
  private readonly root = new Container();
  private readonly bands = new Graphics();
  /**
   * The furnished rooms (ADR-0018 steps 3–4), one per floor, behind everything
   * that carries a rule. Faded: the tickets stay the loudest thing on screen.
   */
  private readonly roomLayer = new Container();
  private roomSprites: Sprite[] = [];
  /** The building's materials (ADR-0026): its walls, façade, slabs and shaft. */
  private readonly atmosphere: Atmosphere;
  /** The building's own lift, finished its own way (ADR-0027). */
  private readonly look: LiftLook;
  /** The floor at street level, which floors are named from (ADR-0028). */
  private readonly ground: number;
  /** What stands in front of the rooms: the façade's right edge, ceiling lights. */
  private readonly front = new Graphics();
  /** The emergency's light on the building's outside, and its beacons (ADR-0029). */
  private readonly alarm = new Graphics();
  private readonly beacons = new Graphics();
  /**
   * The departure boards (ADR-0031, ADR-0051): on the platform while a train is
   * awaited, in the hotel's lobby for its shuttle and wedding car, and on its
   * top floor while breakfast is served. One a floor, two floors at most.
   */
  private readonly departures = new Graphics();
  private readonly departureLabels: Text[] = [];
  /** Choosing who takes the stairs (ADR-0055): those who can wear a ring. */
  stairsPick = false;
  /**
   * The buttons stacked in the bottom-right corner, the power-up and Clear
   * stops, measured from that corner. A row they would cover ends short of
   * them, so its "+N" is never under a button.
   */
  corner = { width: 0, height: 0 };
  /** The player's stops in order (ADR-0052): a number by each floor's badge. */
  private readonly stopNumbers: Text[] = [];
  /** What moves in the rooms: leaves, screens, the lobby clock, the train. */
  private readonly life: RoomLife;
  /** The hotel's rugs: one per guest a landing may hold, under the waiting row itself. */
  private readonly rugs = new Graphics();
  private readonly rules: BuildingRules;
  /** Floor number badges, coloured to match the chips that name them. */
  private readonly badges = new Graphics();
  /** Pulsing outline on floors worth visiting, shown when the player stalls. */
  private readonly hints = new Graphics();
  private readonly structure = new Graphics();
  /** Every car's rig, the first first (ADR-0034). */
  private readonly cars = new Container();
  private readonly rigs: CarRig[] = [];
  /**
   * The car a tap on a floor sends (ADR-0034): the one the player chose last,
   * by tapping it or its shaft. Presentation only: every command names its car.
   */
  selectedCar = 0;
  private readonly floorLabels: Text[] = [];
  private readonly hitAreas: Graphics[] = [];
  private readonly chips = new Map<string, Chip>();
  /**
   * "3 seats needed, 1 free" — shown only for a waiting group the cabin cannot
   * take whole, while there is still room for *someone*. The rule was already
   * right; the doors just opened on a party of three with one free seat and
   * said nothing, which reads as three passengers refusing to board.
   */
  private readonly overflowLabels: Text[] = [];
  private readonly crowdPills: CrowdPill[] = [];
  /**
   * Floor tap targets, kept BELOW the chips on purpose.
   *
   * They were above for the whole of v0.1 and v0.2, which meant a tap aimed at a
   * passenger landed on the floor behind them and select mode silently did
   * nothing. Anything added here has to stay under `chipLayer`.
   */
  private readonly floorHits = new Container();
  private readonly floorLabelLayer = new Container();
  private readonly chipLayer = new Container();
  /** Words about the people on a floor, kept over them so a figure never hides one. */
  private readonly labelLayer = new Container();
  /** A ring round someone just introduced (ADR-0036), over the people. */
  private readonly pointer = new Graphics();
  private pointed: { readonly id: string; readonly untilMs: number } | null = null;
  /** The guided first shifts' lessons (ADR-0030), over everything else. */
  private readonly coach: CoachOverlay;

  private width = 0;
  private height = 0;
  private floorHeight = 0;
  /** How much of a person fits on a floor this tall. Riders are always tickets. */
  private build: Build | null = null;
  /** The view's own clock, in ms: what people move by. Stops when the game does. */
  private clock = 0;
  /** How long this frame was, for walking pace. */
  private frameMs = 16;
  /** Who got out at each floor lately, for a cheer when several do at once (ADR-0037). */
  private readonly stopRuns = new Map<number, { count: number; at: number }>();
  /** Deliveries since the last loss, and the losses already counted. */
  private streak = 0;
  private lossesSeen = 0;
  /** Riders whose arrival at their floor has been shown, so it is shown once. */
  private readonly alighted = new Set<string>();
  /** Who rides in which car, as of the last frame: where a lost rider was lost. */
  private readonly riderCar = new Map<string, number>();
  /** Floors the lift is open at or heading for: the people there look at it. */
  private liftFloors: readonly (number | null)[] = [];
  /** How many more people the run can lose; at 0, those running out of time say so. */
  private spare = Number.POSITIVE_INFINITY;
  /** Losses already scored, so each is shown once. */
  private readonly lost = new Set<string>();
  /** Rings where taps land, and what each stop earned. */
  private readonly feedback: Feedback;
  /**
   * A few words beside the people they are about, said once and gone: what
   * losing someone would do, while there is still time to act.
   */
  private readonly notice: Text;
  /** Which floor the notice is about, and until when it shows, on the view's clock. */
  private noticeFloor: (() => number) | null = null;
  private noticeUntil = 0;
  /** Whether this run has already said what a loss would do. */
  private warnedLoss = false;
  /** Where each floor's waiting row ends this frame, so a notice can follow it. */
  private readonly rowEnds = new Map<number, number>();
  /** A phone set to reduce motion: people stand where they are and simply go. */
  private readonly still =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** A floor was tapped: send `car` there. */
  onFloorTap: (floor: number, car: number) => void = () => {};
  /** A waiting person was tapped, with the car it would send. */
  onPassengerTap: (id: string, car: number) => void = () => {};
  /** A lesson was answered: with the floor it asked for, or null for "go on". */
  onCoachTap: (floor: number | null) => void = () => {};

  constructor(
    private readonly app: Application,
    private readonly level: LevelDef,
  ) {
    this.rules = rulesFor(level.buildingId);
    this.atmosphere = atmosphereOf(level.buildingId);
    this.look = liftLookOf(level.buildingId);
    this.ground = groundOf(level.buildingId);
    this.life = new RoomLife(ROOM_ALPHA, this.still);
    this.feedback = new Feedback(this.still);
    this.root.addChild(
      this.bands,
      this.roomLayer,
      this.life.layer,
      this.front,
      this.alarm,
      this.rugs,
      this.beacons,
      this.badges,
      this.floorLabelLayer,
      this.structure,
      this.hints,
      this.floorHits,
      this.chipLayer,
      // In front of whoever waits under it: a lobby of five hid the shuttle's
      // board behind their heads, and its words on a grey head could not be read.
      this.departures,
      this.labelLayer,
      this.cars,
      this.feedback.layer,
    );
    this.app.stage.addChild(this.root);
    void this.loadRooms();

    for (let floor = 0; floor < level.floors; floor += 1) {
      const hit = new Graphics();
      hit.eventMode = 'static';
      hit.cursor = 'pointer';
      hit.label = `floor-${floor}`;
      hit.on('pointertap', (event) => {
        this.feedback.tap(event.global.x, event.global.y, this.clock);
        const car = this.carForTap(event.global.x, event.global.y);
        if (car.send) this.onFloorTap(floor, car.index);
      });
      this.hitAreas.push(hit);
      this.floorHits.addChild(hit);

      const label = new Text({
        text: floorName(floor, this.ground),
        // Cream on a coloured badge, exactly like a passenger chip — the whole
        // point is that the two read as the same object.
        style: { ...CHIP_FONT, fontSize: 13, fill: COLORS.chipText },
      });
      label.anchor.set(0.5);
      this.floorLabels.push(label);
      this.floorLabelLayer.addChild(label);

      const overflow = new Text({
        text: '',
        // Haloed: on a floor of people it sits over legs or hair, not empty wall.
        style: {
          ...CHIP_FONT,
          fontSize: 11,
          fill: COLORS.group,
          stroke: { color: COLORS.background, width: 4 },
        },
      });
      overflow.anchor.set(0, 0.5);
      overflow.visible = false;
      this.overflowLabels.push(overflow);
      this.labelLayer.addChild(overflow);

      const pill = new Container();
      const pillBg = new Graphics();
      const pillLabel = new Text({
        text: '',
        style: { ...CHIP_FONT, fontSize: 13, fill: COLORS.ink },
      });
      pillLabel.anchor.set(0.5);
      pill.addChild(pillBg, pillLabel);
      pill.visible = false;
      this.crowdPills.push({ root: pill, bg: pillBg, label: pillLabel });
      this.chipLayer.addChild(pill);
    }

    for (let car = 0; car < 1 + (level.extraCars?.length ?? 0); car += 1) {
      this.rigs.push(this.newRig());
    }

    for (let n = 0; n < this.rigs.length * MAX_STOPS; n += 1) {
      const number = new Text({
        text: '',
        style: { ...CHIP_FONT, fontSize: 10, fill: COLORS.chipText },
      });
      number.anchor.set(0.5);
      number.visible = false;
      this.labelLayer.addChild(number);
      this.stopNumbers.push(number);
    }

    for (let board = 0; board < 2; board += 1) {
      const label = new Text({
        text: '',
        style: { ...CHIP_FONT, fontSize: 12, fill: DEPARTURES.text, letterSpacing: 0.5 },
      });
      label.anchor.set(0.5);
      label.visible = false;
      this.labelLayer.addChild(label);
      this.departureLabels.push(label);
    }

    this.labelLayer.addChild(this.pointer);

    this.coach = new CoachOverlay(this.still);
    this.coach.onTap = (x, y) => {
      const step = this.coach.showing;
      if (!step) return;
      if (step.tap === null) {
        this.onCoachTap(null);
      } else if (this.coach.onTarget(y)) {
        this.feedback.tap(x, y, this.clock);
        this.onCoachTap(step.tap);
      }
    };
    this.root.addChild(this.coach.layer);

    this.notice = new Text({
      text: '',
      style: {
        ...CHIP_FONT,
        fontSize: 12,
        fill: COLORS.cabinFull,
        stroke: { color: COLORS.background, width: 4 },
        wordWrap: true,
        wordWrapWidth: 150,
        lineHeight: 14,
      },
    });
    this.notice.anchor.set(0, 0.5);
    this.notice.visible = false;
    this.labelLayer.addChild(this.notice);
  }

  /** Say a few words on a floor for a while (`floor` is asked every frame, so it can follow someone). */
  /** A line beside a floor for a while: what the game wants said, not a rule. */
  tell(text: string, floor: number, ms: number): void {
    this.say(text, () => floor, ms);
  }

  private say(text: string, floor: () => number, ms: number): void {
    this.notice.text = text;
    this.noticeFloor = floor;
    this.noticeUntil = this.clock + ms;
  }

  /** After the waiting row if there is room, otherwise along the top of the floor. */
  private placeNotice(): void {
    const left = this.noticeUntil - this.clock;
    if (!this.noticeFloor || left <= 0) {
      this.notice.visible = false;
      this.noticeFloor = null;
      return;
    }
    const floor = this.noticeFloor();
    const rowStart = this.shaftRight + LAYOUT.aisle;
    const end = (this.rowEnds.get(floor) ?? rowStart) + 4;
    const room = this.width - end - 6;
    if (room >= 110) {
      this.notice.style.wordWrapWidth = Math.min(210, room);
      this.notice.position.set(end, this.floorY(floor));
    } else {
      this.notice.style.wordWrapWidth = this.width - rowStart - 8;
      this.notice.position.set(rowStart + 14, this.floorY(floor) - this.floorHeight / 2 + 10);
    }
    this.notice.alpha = Math.min(1, left / 400);
    this.notice.visible = true;
  }

  /**
   * The first time a run with no losses to spare has someone running out of
   * time, say what losing them would do — beside them, so the warning sign on
   * their ticket and the words read together.
   */
  private warnOfLoss(state: GameState): void {
    if (this.warnedLoss || this.spare !== 0) return;
    const atRisk = state.passengers.find((p) => runningOut(state, p));
    if (!atRisk) return;
    this.warnedLoss = true;
    const text =
      this.level.objective.kind === 'deliverAll'
        ? 'Lose anyone and the shift ends'
        : 'One more loss ends the shift';
    this.say(
      text,
      () =>
        atRisk.state === 'riding'
          ? Math.round(this.carOf(state, atRisk.id).positionMilli / MILLI_PER_FLOOR)
          : atRisk.standingFloor,
      4500,
    );
  }

  /** Point someone out on their floor for a while: the person a card introduces. */
  pointAt(passengerId: string, ms = POINT_MS): void {
    this.pointed = { id: passengerId, untilMs: this.clock + ms };
  }

  /** The ring round whoever was pointed out, while they are still waiting to be collected. */
  private drawPointer(state: GameState): void {
    this.pointer.clear();
    const pointed = this.pointed;
    if (!pointed) return;
    const passenger = state.passengers.find((p) => p.id === pointed.id);
    const left = pointed.untilMs - this.clock;
    if (passenger?.state !== 'waiting' || left <= 0) {
      this.pointed = null;
      return;
    }
    const top = this.floorY(passenger.standingFloor) - this.floorHeight / 2 + 3;
    const chip = this.chips.get(passenger.id);
    // Off the end of a crowded row: the start of the row, where the queue is.
    const x = chip?.root.visible ? chip.root.x - 6 : this.shaftRight + LAYOUT.aisle - 6;
    const w = chip?.root.visible ? chip.width + 12 : LAYOUT.passengerSize + 12;
    const breath = this.still ? 1 : 0.55 + 0.45 * Math.sin((this.clock / 700) * Math.PI * 2);
    this.pointer
      .roundRect(x, top, w, this.floorHeight - 6, 10)
      .stroke({ width: 3, color: COLORS.target, alpha: Math.min(1, left / 400) * breath });
  }

  /** Show a lesson (ADR-0030), or clear it with null. While one shows, it takes every tap. */
  setCoach(step: CoachStep | null): void {
    this.coach.show(step);
  }

  /** Rasterise this level's rooms, then lay them out. The picture never waits on it. */
  private async loadRooms(): Promise<void> {
    const kinds = Array.from({ length: this.level.floors }, (_, floor) =>
      roomForFloor(this.level.buildingId, this.rules, floor, this.level.floors),
    );
    // A room that will not rasterise leaves a plain floor: scenery, never a rule.
    this.life.load(kinds).catch(() => {});
    try {
      const textures = await Promise.all(kinds.map((kind) => roomTexture(kind)));
      if (this.root.destroyed) return;
      this.roomSprites = textures.map((texture) => {
        const sprite = new Sprite(texture);
        sprite.alpha = ROOM_ALPHA;
        this.roomLayer.addChild(sprite);
        return sprite;
      });
      this.layoutRooms();
    } catch {
      // As above: the floor stays plain.
    }
  }

  /**
   * Each room stands on its floor's slab, scaled to the floor, against the
   * right-hand wall — the left is where people wait, beside the shaft.
   */
  private layoutRooms(): void {
    const scale = Math.max(0, this.floorHeight - 2) / 96;
    const frames: RoomFrame[] = Array.from({ length: this.level.floors }, (_, floor) => ({
      x: this.width - 240 * scale,
      y: this.floorY(floor) - this.floorHeight / 2,
      scale,
    }));
    this.roomSprites.forEach((sprite, floor) => {
      const frame = frames[floor];
      if (!frame) return;
      sprite.width = 240 * scale;
      sprite.height = 96 * scale;
      sprite.position.set(frame.x, frame.y);
    });
    this.life.layout(frames);
  }

  private drawRugs(): void {
    const { rugs } = this;
    rugs.clear();
    const size = LAYOUT.passengerSize;
    for (let floor = 0; floor < this.level.floors; floor += 1) {
      const count = rugsOn(this.rules, floor);
      const y = this.floorY(floor) + this.floorHeight / 2 - 4;
      for (let i = 0; i < count; i += 1) {
        const cx = this.shaftRight + LAYOUT.aisle + size / 2 + i * (size + LAYOUT.passengerGap);
        rugs.ellipse(cx, y, size * 0.6, 3.5).fill({ color: LIFT_PARTS.rug });
      }
    }
  }

  /** Height the floors are laid out in, once the control strips are reserved. */
  private get usableHeight(): number {
    return Math.max(0, this.height - LAYOUT.insetTop - LAYOUT.insetBottom);
  }

  /** Y of the centre of a floor. Floor 0 sits at the bottom, as a building does. */
  private floorY(floor: number): number {
    return LAYOUT.insetTop + this.usableHeight - (floor + 0.5) * this.floorHeight;
  }

  /** Each shaft's width: narrower when two stand side by side (ADR-0034). */
  private get shaftWidth(): number {
    return this.rigs.length > 1 ? LAYOUT.twinShaftWidth : LAYOUT.shaftWidth;
  }

  private shaftLeftOf(car: number): number {
    return LAYOUT.gutter + car * this.shaftWidth;
  }

  /** The right edge of the bank: where the floors begin. */
  private get shaftRight(): number {
    return LAYOUT.gutter + this.rigs.length * this.shaftWidth;
  }

  /** A car's cabin, label and stop tickets, ready to draw into. */
  private newRig(): CarRig {
    const label = new Text({
      text: '',
      // The cable runs straight through where this sits; the halo keeps it legible.
      style: {
        ...CHIP_FONT,
        fontSize: 13,
        fill: COLORS.textDim,
        stroke: { color: COLORS.background, width: 4 },
      },
    });
    label.anchor.set(0.5);
    const layer = new Container();
    layer.addChild(label);
    const cabin = new Graphics();
    const doors = new Graphics();
    this.cars.addChild(cabin, layer, doors);
    return {
      cabin,
      layer,
      label,
      doors,
      stopChips: new Map(),
      moreStops: null,
      box: null,
      body: null,
    };
  }

  /**
   * Which car a tap on a floor is for (ADR-0034). With one car, always it. With
   * a bank: a tap on a car chooses it and sends nothing; a tap elsewhere in its
   * shaft chooses it and sends it to that floor; anywhere else on the floor sends
   * the car last chosen.
   */
  private carForTap(x: number, y: number): { index: number; send: boolean } {
    if (this.rigs.length === 1) return { index: 0, send: true };
    for (let car = 0; car < this.rigs.length; car += 1) {
      const left = this.shaftLeftOf(car);
      if (x < left || x >= left + this.shaftWidth) continue;
      this.selectedCar = car;
      const body = this.rigs[car]?.body;
      const onIt = body && y >= body.y && y <= body.y + body.h;
      return { index: car, send: !onIt };
    }
    return { index: this.selectedCar, send: true };
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.floorHeight = this.usableHeight / this.level.floors;
    this.build = buildFor(this.floorHeight, LAYOUT.passengerSize);
    this.drawBands();
    this.layoutRooms();
    this.drawRugs();
  }

  private drawBands(): void {
    const { bands, badges, floorHeight, front, width } = this;
    const atmosphere = this.atmosphere;
    bands.clear();
    badges.clear();
    front.clear();

    // The reserved strips are part of the picture, not a gap in it. Paint the
    // whole surface first so they read as the frame around the building rather
    // than as a floor that failed to draw.
    bands.rect(0, 0, width, this.height).fill({ color: COLORS.background });

    // Bottom floor first: each slab hangs below its floor's surface, over the
    // ceiling of the floor beneath, so it has to be drawn after that floor.
    for (let floor = 0; floor < this.level.floors; floor += 1) {
      const top = this.floorY(floor) - floorHeight / 2;
      // The building's own wall: its material, not a shared cream (ADR-0026).
      const wall = {
        x: this.shaftRight + LAYOUT.aisle,
        top,
        right: width - FACADE_EDGE,
        height: floorHeight - 2,
      };
      bands.rect(0, top, width, floorHeight).fill({ color: atmosphere.wall });
      atmosphere.paintWall(bands, wall);
      // Each floor wears a wash of its own colour rather than an alternating
      // grey. A passenger's chip and the floor they are going to then match at a
      // glance, across the whole screen, not just on the number badge.
      // Light enough to stay a wash rather than a colour scheme — the chips are
      // the saturated thing on screen and they have to stay the loudest.
      bands.rect(0, top, width, floorHeight).fill({ color: floorColor(floor), alpha: 0.09 });
      // Under the street the light is the lamps', not the day's (ADR-0028): a
      // little dimmer, never enough to dull a ticket.
      if (floor < this.ground) {
        bands.rect(wall.x, top, wall.right - wall.x, floorHeight).fill({
          color: EARTH.root,
          alpha: 0.08,
        });
      }
      // The façade frames the floors: the number column on the left and the
      // right-hand edge are the building's outside.
      // Below the street the outside is the ground, not the building (ADR-0028).
      const depth = this.ground - floor;
      const outside = (strip: FacadeStrip): void =>
        depth > 0 ? paintEarth(bands, strip, depth) : atmosphere.paintFacade(bands, strip);
      outside({ x: 0, top, width: LAYOUT.gutter, height: floorHeight, side: 'left' });
      const right: FacadeStrip = {
        x: width - FACADE_EDGE,
        top,
        width: FACADE_EDGE,
        height: floorHeight,
        side: 'right',
      };
      if (depth > 0) paintEarth(front, right, depth);
      else atmosphere.paintFacade(front, right);
      atmosphere.paintCeiling?.(front, { ...wall, top: top + atmosphere.slabHeight - 2 });
      // The slab each storey stands on — what makes the stack read as a cutaway
      // building rather than as stripes. Its top 2px are the floor people stand
      // on; the rest hangs below, made of the façade's material.
      bands
        .rect(0, top + floorHeight - 2, width, atmosphere.slabHeight)
        .fill({ color: atmosphere.slab });
      // Where the street crosses the building's outside: the pavement, at the
      // top of the first floor underground.
      if (depth === 1) {
        paintPavement(bands, 0, top, LAYOUT.gutter);
        paintPavement(front, width - FACADE_EDGE, top, FACADE_EDGE);
      }

      const label = this.floorLabels[floor];
      if (label) {
        label.x = LAYOUT.gutter / 2;
        label.y = this.floorY(floor);
      }

      // A blind tester read the number on a chip as decoration rather than as a
      // destination, and visited three wrong floors before the right one. Giving
      // the floor the same colour turns "read 3, find 3" into matching a colour.
      const badgeSize = Math.min(24, floorHeight - 6);
      badges
        .circle(LAYOUT.gutter / 2, this.floorY(floor), badgeSize / 2)
        .fill({ color: floorColor(floor) })
        .stroke({ width: 1.5, color: COLORS.ink });

      const hit = this.hitAreas[floor];
      if (hit) {
        hit.clear();
        hit.rect(0, top, width, floorHeight).fill({ color: 0xffffff, alpha: 0.001 });
      }
    }
  }

  /** The car someone rides in, or rode in last frame; the first car if neither. */
  private carOf(state: GameState, passengerId: string): Elevator {
    const cars = carsOf(state);
    return (
      cars.find((car) => car.riders.includes(passengerId)) ??
      cars[this.riderCar.get(passengerId) ?? 0] ??
      state.elevator
    );
  }

  /** 0 = shut, 1 = fully open. Drives the cabin aperture. */
  private doorOpenness(state: GameState, elevator: Elevator): number {
    const { spec } = state;
    switch (elevator.doorPhase) {
      case 'open':
        return 1;
      case 'opening':
        return 1 - Math.max(0, elevator.phaseTimerMs) / spec.doorOpenMs;
      case 'closing':
        return Math.max(0, elevator.phaseTimerMs) / spec.doorCloseMs;
      case 'closed':
        return 0;
    }
  }

  update(session: Session, deltaMs = 16): void {
    if (this.height === 0) return;
    this.frameMs = Math.min(100, Math.max(0, deltaMs));
    this.clock += this.frameMs;
    const { state } = session;
    // Every car's (ADR-0034): where each is open, and where each is going.
    this.liftFloors = carsOf(state).flatMap((car) => [servicingAt(state, car), car.targetFloor]);
    this.spare = lossesToSpare(state, this.level.objective);
    const clock = clockFor(state.elapsedMs, state.timeLimitMs, this.level.objective);
    this.life.update(this.clock, state, 1 - clock.remainingMs / Math.max(1, clock.totalMs));
    this.drawShaft(session.state);
    this.drawEmergencies(state);
    this.drawDepartures(state);
    this.drawHints(session);
    carsOf(state).forEach((car, index) => {
      const rig = this.rigs[index];
      if (rig) this.drawCar(rig, state, car, index);
    });
    this.syncChips(session);
    this.drawPointer(state);
    this.scoreLosses(state);
    this.warnOfLoss(state);
    this.placeNotice();
    this.feedback.update(this.clock);
    this.drawCoach();
    this.riderCar.clear();
    carsOf(state).forEach((car, index) => {
      for (const id of car.riders) this.riderCar.set(id, index);
    });
  }

  /** Each departure someone is still running for, counting down on its floor (ADR-0031, ADR-0051). */
  private drawDepartures(state: GameState): void {
    this.departures.clear();
    const awaited = departures(state);
    this.departureLabels.forEach((label, i) => {
      const next = awaited[i];
      label.visible = next !== undefined;
      if (!next) return;
      // Breakfast's board is on the top floor, where the bonus goal sits over the
      // room's top right: it hangs below it there.
      const drop = next.platform === this.level.floors - 1 ? 34 : 9;
      const top = this.floorY(next.platform) - this.floorHeight / 2 + drop;
      const x = this.width - FACADE_EDGE - (next.word.length > 6 ? 108 : 90);
      paintBoard(
        this.departures,
        label,
        x,
        top,
        next.atMs - state.elapsedMs,
        this.clock,
        this.still,
        next.word,
      );
    });
  }

  /**
   * While an emergency is under way (ADR-0029): the building's outside flashes
   * in the crew's colours, and the incident's floor has a beacon, with a ring
   * that fills while the crew is in there. All of it gone once they have left.
   */
  private drawEmergencies(state: GameState): void {
    this.alarm.clear();
    this.beacons.clear();
    const open = underWay(state);
    if (open.length === 0) return;
    // The police's colours when both are here: the blue is the one that is new.
    const kind = open.some((o) => o.kind === 'police') ? 'police' : 'ambulance';
    const lit = sirenPair(kind)[sirenPhase(this.clock, this.still)] as number;
    const top = this.floorY(this.level.floors - 1) - this.floorHeight / 2;
    const tall = this.floorHeight * this.level.floors;
    this.alarm.rect(0, top, LAYOUT.gutter, tall).fill({ color: lit, alpha: 0.24 });
    this.alarm
      .rect(this.width - FACADE_EDGE, top, FACADE_EDGE, tall)
      .fill({ color: lit, alpha: 0.32 });
    for (const { incident, kind: crew } of open) {
      const x = this.width - FACADE_EDGE - 30;
      const y = this.floorY(incident.floor) - this.floorHeight / 2 + 7;
      paintBeacon(this.beacons, x, y, crew, this.clock, this.still);
      if (incident.phase === 'attending') {
        const stay = incident.crew.callout?.stayMs ?? 1;
        paintReadyRing(this.beacons, x - 38, y + 14, 1 - incident.readyInMs / stay);
      }
    }
  }

  /** On a level graded on service, what a stop earns or costs rises off it. */
  private get scored(): boolean {
    return this.level.stars.metric === 'service';
  }

  /** Where a stop's score rises from: just beside the shaft, high on the floor. */
  private scoreAt(floor: number, points: number, key = `stop-${floor}`): void {
    if (!this.scored) return;
    const y = this.floorY(floor) - this.floorHeight * 0.28;
    this.feedback.score(points, key, this.shaftRight + 4, y, this.clock);
  }

  /**
   * Someone just lost: what it cost, over them. Whoever gave up waiting takes
   * it with them as they walk off — left at the head of the queue, it sat over
   * the next person to step up (PLAYTEST_LOG, 2026-10-20). Lost aboard, it is
   * the car's; turned away on arrival, they were never on screen to carry it.
   */
  private scoreLosses(state: GameState): void {
    for (const passenger of state.passengers) {
      if (passenger.state !== 'abandoned' || this.lost.has(passenger.id)) continue;
      this.lost.add(passenger.id);
      if (passenger.abandonedMs === null || state.elapsedMs - passenger.abandonedMs > 400) continue;
      const cost = -costOfLoss(passenger, passenger.lostReason ?? 'gave-up');
      const key = `lost-${passenger.id}`;
      if (passenger.boardedMs === null && this.scoreWalkingOff(passenger, cost, key)) continue;
      const floor =
        passenger.boardedMs !== null
          ? Math.round(this.carOf(state, passenger.id).positionMilli / MILLI_PER_FLOOR)
          : passenger.standingFloor;
      this.scoreAt(floor, cost, key);
    }
  }

  /** A loss over someone leaving the floor, going with them. False if they are not on screen. */
  private scoreWalkingOff(passenger: Passenger, cost: number, key: string): boolean {
    const chip = this.chips.get(passenger.id);
    if (!chip || !this.scored) return false;
    const y = this.floorY(passenger.standingFloor) - this.floorHeight * 0.28;
    const exit = chip.exit?.kind === 'storm' ? chip.exit : null;
    const from = exit ? exit.from : chip.x;
    const walk = exit ? { dx: exit.to - exit.from, overMs: EXIT_MS.storm } : null;
    this.feedback.score(cost, key, from + chip.width / 2, y, this.clock, walk);
    return true;
  }

  /** Light what the lesson is about, and put the hand on the floor it asks for. */
  private drawCoach(): void {
    const step = this.coach.showing;
    if (!step) return;
    const target = step.tap === null ? null : this.floorBox(step.tap);
    this.coach.draw(
      {
        width: this.width,
        height: this.height,
        holes: step.spots.map((spot) => this.spotBox(spot)),
        target,
        // On the row, in the open space past the waiting line.
        touch:
          step.tap === null
            ? null
            : {
                x: Math.min(this.width - 70, this.shaftRight + LAYOUT.aisle + 120),
                y: this.floorY(step.tap),
              },
      },
      this.clock,
    );
  }

  private floorBox(floor: number): Box {
    const top = this.floorY(floor) - this.floorHeight / 2;
    return { x: 2, y: top + 1, w: this.width - 4, h: this.floorHeight - 2 };
  }

  private spotBox(spot: Spot): Box {
    if (spot.kind === 'floor') return this.floorBox(spot.floor);
    if (spot.kind === 'cabin') {
      return this.rigs[spot.car ?? 0]?.box ?? this.floorBox(0);
    }
    // The waiting line, from the shaft to its last person.
    const top = this.floorY(spot.floor) - this.floorHeight / 2;
    const start = this.shaftRight + 2;
    const end = Math.max(
      this.rowEnds.get(spot.floor) ?? 0,
      start + LAYOUT.aisle + LAYOUT.passengerSize * 2,
    );
    return {
      x: start,
      y: top + 2,
      w: Math.min(this.width - 4, end + 10) - start,
      h: this.floorHeight - 4,
    };
  }

  /**
   * When the player stops acting, outline the floors that are worth visiting.
   *
   * Not a tutorial and not an answer — it says "these are the things you can
   * tap", and lets the level decide how much that gives away. On the first level
   * exactly one floor qualifies, so it *is* the answer; later, several do, and it
   * is only a reminder of where the choices are.
   *
   * Earned its place: a blind tester spent 35 seconds on a level that opens with
   * an empty ground floor, because nothing on screen moved until they guessed.
   */
  private drawHints(session: Session): void {
    const { hints } = this;
    hints.clear();

    const state = session.state;
    if (state.status !== 'playing') return;
    if (session.idleMs < HINT_AFTER_MS) return;
    // Only while some car stands at a floor, waiting to be told (every car, ADR-0034).
    const cars = carsOf(state);
    const standing = cars.filter((car) => isParked(state, car) || servicingAt(state, car) !== null);
    if (standing.length === 0) return;

    const worthVisiting = new Set<number>();
    for (const p of state.passengers) {
      if (p.state === 'waiting' && canBoard(state, p, p.standingFloor)) {
        worthVisiting.add(p.standingFloor);
      } else if (p.state === 'riding') {
        // A round's floors first (ADR-0031); the destination once it is done.
        for (const floor of nextStops(state, p)) worthVisiting.add(floor);
      }
    }
    for (const car of cars) {
      if (isParked(state, car)) worthVisiting.delete(currentFloor(state, car) ?? -1);
    }
    if (worthVisiting.size === 0) return;

    const since = session.idleMs - HINT_AFTER_MS;
    const alpha =
      since < HINT_PULSE_MS * HINT_PULSES
        ? // A slow breath rather than a blink: noticeable in peripheral vision.
          0.25 + ((Math.sin((since / HINT_PULSE_MS) * Math.PI * 2 - Math.PI / 2) + 1) / 2) * 0.5
        : // Then it stops moving and simply stays.
          0.3;

    for (const floor of worthVisiting) {
      const top = this.floorY(floor) - this.floorHeight / 2;
      hints
        .roundRect(2, top + 2, this.width - 4, this.floorHeight - 4, 6)
        .stroke({ width: 2, color: COLORS.target, alpha });
    }
  }

  private drawShaft(state: GameState): void {
    const { structure } = this;
    structure.clear();
    const top = LAYOUT.insetTop;
    const tall = this.usableHeight;
    const atmosphere = this.atmosphere;
    const width = this.shaftWidth;
    const cars = carsOf(state);
    const bank = cars.length > 1;

    for (let car = 0; car < cars.length; car += 1) {
      const left = this.shaftLeftOf(car);
      // Dark, and the building's own (ADR-0026): the car is the light thing in it.
      structure.rect(left, top, width, tall).fill({ color: atmosphere.shaft });
      atmosphere.paintShaft(structure, {
        left,
        right: left + width,
        top,
        height: tall,
        slabs: Array.from(
          { length: this.level.floors },
          (_, floor) => this.floorY(floor) + this.floorHeight / 2 - 1,
        ),
      });

      // Guide rails. Without them the shaft is an empty rectangle and the car
      // reads as floating rather than running in a track.
      for (const offset of [22, width - 24]) {
        structure.rect(left + offset, top, 2, tall).fill({ color: atmosphere.rail, alpha: 0.6 });
      }
    }

    this.drawStops(state);
    for (const [index, car] of cars.entries()) {
      const target = car.targetFloor;
      if (target === null) continue;
      const floorTop = this.floorY(target) - this.floorHeight / 2;
      structure
        .rect(0, floorTop, this.width, this.floorHeight)
        .fill({ color: COLORS.target, alpha: 0.1 });
      structure.rect(0, floorTop, 4, this.floorHeight).fill({ color: COLORS.target });
      // In a bank, which car is going there: its landing, marked in its shaft.
      if (bank) {
        structure
          .roundRect(this.shaftLeftOf(index) + 3, floorTop + 3, width - 6, this.floorHeight - 6, 6)
          .stroke({ width: 2, color: COLORS.target, alpha: 0.8 });
      }
    }

    for (let car = 0; car < cars.length; car += 1) {
      const left = this.shaftLeftOf(car);
      structure.rect(left, top, 2, tall).fill({ color: atmosphere.shaftEdge });
      structure.rect(left + width - 2, top, 2, tall).fill({ color: atmosphere.shaftEdge });
    }
    // The car a floor tap sends (ADR-0034), framed in the light a target wears.
    if (bank) {
      structure
        .rect(this.shaftLeftOf(this.selectedCar) + 1, top + 1, width - 2, tall - 2)
        .stroke({ width: 3, color: COLORS.target });
    }
    this.drawLanterns();
  }

  /**
   * The player's stops (ADR-0052): each floor after the one the car is going to
   * lit as a target is, fainter, and every floor of the plan numbered in order
   * in that car's own shaft, so a queue reads as one, and two lifts' queues as
   * two (ADR-0053).
   */
  private drawStops(state: GameState): void {
    const { structure } = this;
    const cars = carsOf(state);
    const bank = cars.length > 1;
    let used = 0;
    for (const [index, car] of cars.entries()) {
      if (car.stops.length === 0) continue;
      const plan = planOf(state, car);
      const going = plan.length - car.stops.length;
      const left = this.shaftLeftOf(index);
      plan.forEach((floor, order) => {
        const floorTop = this.floorY(floor) - this.floorHeight / 2;
        if (order >= going) {
          if (bank) {
            // Whose stop it is (the owner asked to see which lift goes where):
            // marked in that car's own shaft, as its target is, fainter.
            structure
              .roundRect(left + 3, floorTop + 3, this.shaftWidth - 6, this.floorHeight - 6, 6)
              .stroke({ width: 1.5, color: COLORS.target, alpha: 0.45 });
          } else {
            structure
              .rect(0, floorTop, this.width, this.floorHeight)
              .fill({ color: COLORS.target, alpha: 0.05 });
            structure
              .rect(0, floorTop, 4, this.floorHeight)
              .fill({ color: COLORS.target, alpha: 0.5 });
          }
        }
        // The order, in the car's own shaft: with two lifts, each counts its own.
        const x = left + 12;
        const y = floorTop + 12;
        structure
          .circle(x, y, 7)
          .fill({ color: COLORS.target })
          .stroke({ width: 1, color: COLORS.ink });
        const number = this.stopNumbers[used];
        used += 1;
        if (!number) return;
        number.text = String(order + 1);
        number.position.set(x, y);
        number.visible = true;
      });
    }
    for (let n = used; n < this.stopNumbers.length; n += 1) {
      const number = this.stopNumbers[n];
      if (number) number.visible = false;
    }
  }

  /**
   * A hall lantern over each landing door, lit while the lift is coming to
   * that floor or standing open at it: what the people there are looking at.
   * In the aisle beside the shaft, which nothing else uses at that height.
   */
  private drawLanterns(): void {
    const { structure } = this;
    for (let floor = 0; floor < this.level.floors; floor += 1) {
      const lit = this.liftFloors.includes(floor);
      const x = this.shaftRight + 2;
      const y = this.floorY(floor) - this.floorHeight / 2 + 5;
      if (lit) structure.circle(x + 4, y + 2.5, 7).fill({ color: LIFT_PARTS.glow, alpha: 0.35 });
      structure
        .roundRect(x, y, 8, 5, 2.5)
        .fill({ color: lit ? LIFT_PARTS.glow : LIFT_PARTS.steelDark })
        .stroke({ width: 1, color: COLORS.ink });
    }
  }

  private drawCar(rig: CarRig, state: GameState, car: Elevator, index: number): void {
    const { cabin } = rig;
    const position = car.positionMilli / MILLI_PER_FLOOR;
    // A six-seat cabin is visibly a bigger box than a four-seat one. The plan
    // update asks for visual progression to back up the mechanical kind, and
    // this is the cheapest honest version of it: the thing that holds more is
    // drawn larger.
    const seats = state.spec.capacity;
    const { width, height } = cabinSize(this.floorHeight, seats, this.shaftWidth);
    const x = this.shaftLeftOf(index) + 9;
    const y =
      LAYOUT.insetTop + this.usableHeight - (position + 0.5) * this.floorHeight - height / 2;
    // Squeezed in (ADR-0055): two more seats for a stop, the box no bigger.
    const room = capacityOf(state, car);
    const full = car.load >= room;
    // Status rides on the outline and the capacity label. The doors stay a
    // neutral colour so a full cabin does not turn the whole car into a warning.
    const accent = full ? COLORS.cabinFull : COLORS.cabinEdge;

    cabin.clear();

    // The hoist it hangs from and a shallow roof, so the car reads as a lift on
    // its ropes rather than a box floating in the well — and the ropes run
    // over the sheave as it moves.
    const apexY = y - 6;
    paintHoist(
      cabin,
      x + width / 2,
      LAYOUT.insetTop,
      apexY,
      position * this.floorHeight,
      state.loadout.cable ?? 0,
    );
    cabin
      .moveTo(x + 8, y + 1)
      .lineTo(x + width / 2, apexY)
      .lineTo(x + width - 8, y + 1)
      .closePath()
      .fill({ color: accent });
    // The fitted parts, visible on the car (ADR-0021): the motor on the roof,
    // streaks behind a fast car, a door pattern per tier, the big cabin's trim.
    const { loadout } = state;
    const motorReach = paintMotor(cabin, x + width / 2, apexY, loadout.motor ?? 0);
    const target = car.targetFloor;
    const moving = car.phase === 'moving' && target !== null;
    paintStreaks(
      cabin,
      x,
      y,
      width,
      height,
      loadout.motor ?? 0,
      moving ? Math.sign(target - position) : 0,
    );

    cabin.roundRect(x, y, width, height, 9).fill({ color: COLORS.cabin });

    // Doors part from the centre, so an open aperture reads as "board now".
    // They are drawn over the riders' tickets, see-through while anyone rides.
    const { doors } = rig;
    doors.clear();
    doors.alpha = car.riders.length > 0 ? RIDDEN_DOOR_ALPHA : 1;
    const openness = this.doorOpenness(state, car);
    const half = (width - 8) / 2;
    const panel = half * (1 - openness);
    if (panel > 0.5) {
      const tier = loadout.doors ?? 0;
      doors.rect(x + 4, y + 4, panel, height - 8).fill({ color: COLORS.cabinDoor });
      doors
        .rect(x + 4 + (width - 8) - panel, y + 4, panel, height - 8)
        .fill({ color: COLORS.cabinDoor });
      paintDoorPanel(doors, x + 4, y + 4, panel, height - 8, tier, 'left', this.look);
      paintDoorPanel(
        doors,
        x + 4 + (width - 8) - panel,
        y + 4,
        panel,
        height - 8,
        tier,
        'right',
        this.look,
      );
      // Seam down the middle while shut, so "closed" is unmistakable at a glance.
      if (openness < 0.02) {
        doors.rect(x + width / 2 - 1, y + 4, 2, height - 8).fill({ color: COLORS.ink });
      }
    }

    paintCabinTrim(cabin, x, y, width, height, loadout.cabin ?? 0, this.look);
    cabin.roundRect(x, y, width, height, 9).stroke({ width: 3, color: accent });

    // A word rather than a number once it is full. "6/6" is technically the
    // same information and a playtester read straight past it, then waited
    // several seconds for two more people to board a cabin with no room in it.
    rig.label.text = full ? 'FULL' : `${car.load}/${room}`;
    rig.label.x = x + width / 2;
    rig.label.y = apexY - 9 - motorReach;
    const labelTop = rig.label.y - 12;
    rig.box = { x: x - 8, y: labelTop, w: width + 16, h: y + height + 6 - labelTop };
    rig.body = { x, y: apexY, w: width, h: y + height - apexY };
    rig.label.style.fill = accent;

    this.layoutRiders(rig, state, car, x, y, width, height);
  }

  /**
   * The cabin's stops: one ticket per floor it still owes, highest first, the
   * riders for each stacked behind it. Sized for the stops actually owed, not
   * for every seat, so the numbers stay readable where a ticket per rider did
   * not (KNOWN_ISSUES, 2026-10-14).
   */
  private layoutRiders(
    rig: CarRig,
    state: GameState,
    car: Elevator,
    x: number,
    y: number,
    width: number,
    height: number,
  ): void {
    // A big car on a tall building can owe more stops than fit at a readable
    // size (ADR-0027): the nearest are drawn, the rest become one "+N".
    const {
      shown: stops,
      more,
      grid,
    } = readableStops(
      stopsOwed(state, car.riders),
      car.positionMilli / MILLI_PER_FLOOR,
      width,
      height,
    );
    const blockWidth = grid.columns * grid.width + (grid.columns - 1) * CABIN_GAP;
    const blockHeight = grid.rows * grid.height + (grid.rows - 1) * CABIN_GAP;
    const originX = x + (width - blockWidth) / 2;
    const originY = y + (height - blockHeight) / 2;
    const shown = new Set<number>();

    stops.forEach((group, index) => {
      const chip = this.stopChipFor(rig, group.stop);
      const vip = group.riders.some((p) => p.vip);
      const key = [grid.width, grid.height, grid.font, group.riders.length, vip].join(':');
      if (chip.key !== key) this.paintStopChip(chip, group.stop, grid, group.riders.length, vip);
      chip.key = key;
      chip.root.x = originX + (index % grid.columns) * (grid.width + CABIN_GAP);
      chip.root.y = originY + Math.floor(index / grid.columns) * (grid.height + CABIN_GAP);
      chip.root.visible = true;
      this.paintStopMeter(chip, group.riders, state);
      shown.add(group.stop);
    });
    for (const [stop, chip] of rig.stopChips) {
      if (!shown.has(stop)) chip.root.visible = false;
    }
    const pill = this.moreStopsPill(rig);
    pill.root.visible = more > 0;
    if (more > 0) {
      const index = stops.length;
      const radius = Math.round(Math.min(grid.width, grid.height) * 0.3);
      pill.plate
        .clear()
        .roundRect(0, 0, grid.width, grid.height, radius)
        .fill({ color: COLORS.cabin })
        .stroke({ width: 1.5, color: COLORS.ink });
      pill.label.text = `+${more}`;
      pill.label.style.fontSize = Math.max(10, grid.font - 2);
      pill.label.x = grid.width / 2;
      pill.label.y = grid.height / 2 + 0.5;
      pill.root.x = originX + (index % grid.columns) * (grid.width + CABIN_GAP);
      pill.root.y = originY + Math.floor(index / grid.columns) * (grid.height + CABIN_GAP);
    }
  }

  /** "+N": the stops a big car owes beyond what its cabin can show readably. */
  private moreStopsPill(rig: CarRig): { root: Container; plate: Graphics; label: Text } {
    if (rig.moreStops) return rig.moreStops;
    const root = new Container();
    const plate = new Graphics();
    const label = new Text({
      text: '',
      style: { ...CHIP_FONT, fontSize: 12, fill: COLORS.ink },
    });
    label.anchor.set(0.5);
    root.addChild(plate, label);
    root.label = 'more-stops';
    rig.layer.addChild(root);
    rig.moreStops = { root, plate, label };
    return rig.moreStops;
  }

  private stopChipFor(rig: CarRig, stop: number): StopChip {
    const existing = rig.stopChips.get(stop);
    if (existing) return existing;
    const root = new Container();
    const plate = new Graphics();
    const meter = new Graphics();
    const label = new Text({
      text: floorName(stop, this.ground),
      style: { ...CHIP_FONT, fontSize: 13, fill: COLORS.chipText },
    });
    label.anchor.set(0.5);
    root.addChild(plate, label, meter);
    root.label = `stop-${stop}`;
    rig.layer.addChild(root);
    const chip: StopChip = { root, plate, meter, label, key: '', width: 0, height: 0 };
    rig.stopChips.set(stop, chip);
    return chip;
  }

  private paintStopChip(
    chip: StopChip,
    stop: number,
    grid: { width: number; height: number; font: number },
    riders: number,
    vip: boolean,
  ): void {
    const { width, height } = grid;
    const radius = Math.round(Math.min(width, height) * 0.3);
    chip.width = width;
    chip.height = height;
    chip.plate.clear();
    // The riders for this stop, stacked behind its ticket: one card per extra
    // rider, up to two, so "three people get out here" reads without a number.
    for (let k = Math.min(riders - 1, 2); k >= 1; k -= 1) {
      chip.plate
        .roundRect(k * 2.5, -k * 2.5, width, height, radius)
        .fill({ color: floorColor(stop) })
        .stroke({ width: 1, color: COLORS.ink });
    }
    chip.plate
      .roundRect(0, 0, width, height, radius)
      .fill({ color: floorColor(stop) })
      .stroke({ width: 1.5, color: COLORS.ink });
    if (vip) {
      chip.plate
        .roundRect(-2.5, -2.5, width + 5, height + 5, radius + 2.5)
        .stroke({ width: 2, color: COLORS.vip });
    }
    chip.label.text = floorName(stop, this.ground);
    chip.label.style.fontSize = grid.font;
    chip.label.x = width / 2;
    chip.label.y = height / 2 + 0.5;
  }

  /** A courier's deadline keeps running in the cabin, so the stop shows the most urgent one. */
  private paintStopMeter(chip: StopChip, riders: readonly Passenger[], state: GameState): void {
    chip.meter.clear();
    // A rider in the last seconds of a deadline: the stop's ticket blinks.
    const hurry = riders.some((rider) => inLastSeconds(state, rider));
    chip.root.alpha = hurry && !this.still ? blinkAlpha(this.clock) : 1;
    let fraction: number | null = null;
    for (const rider of riders) {
      const left = deadlineRemaining(state, rider);
      if (left === null) continue;
      const f = deadlineFraction(state, rider) ?? 1;
      if (fraction === null || f < fraction) fraction = f;
    }
    if (fraction === null) return;
    const inset = Math.max(3, Math.round(chip.height * 0.2));
    const colour = fraction > 0.3 ? COLORS.courier : COLORS.patienceOut;
    this.drawBar(chip.meter, inset, 1.5, chip.width - inset * 2, fraction, colour);
    if (riders.some((rider) => (this.spare === 0 || rider.critical) && runningOut(state, rider))) {
      paintWarning(chip.meter, chip.width, 0, this.clock);
    }
  }

  /**
   * How visible someone waiting is: fading in as they arrive, and blinking in
   * the last seconds of their clock. Held still, the warning sign instead.
   */
  private waitingAlpha(chip: Chip, passenger: Passenger, state: GameState): number {
    const arriving = this.still ? 1 : Math.min(1, (this.clock - chip.bornMs) / 200);
    if (!inLastSeconds(state, passenger)) return arriving;
    if (this.still) {
      paintWarning(chip.meter, chip.width + 1, -2, this.clock);
      return arriving;
    }
    return arriving * blinkAlpha(this.clock);
  }

  private syncChips(session: Session): void {
    const state = session.state;
    const open = openDoors(state);
    const selectable = state.mode === 'select' && open.size > 0;
    const queuedAnywhere = new Set(carsOf(state).flatMap((car) => car.boardQueue));
    const byFloor = new Map<number, Passenger[]>();
    const visible = new Set<string>();
    this.rowEnds.clear();

    for (const passenger of state.passengers) {
      if (passenger.state !== 'waiting') continue;
      const list = byFloor.get(passenger.standingFloor);
      if (list) list.push(passenger);
      else byFloor.set(passenger.standingFloor, [passenger]);
    }

    const size = LAYOUT.passengerSize;
    const shownOverflow = new Set<number>();

    const shownCrowd = new Set<number>();

    for (const [floor, list] of byFloor) {
      // The order the lift takes them in, whoever came first (the owner saw a
      // crew drawn behind people who came after it, PLAYTEST_LOG 2026-10-29).
      const waiting = inArrivalOrder(list);
      let cursorX = this.shaftRight + LAYOUT.aisle;
      const shown = this.rowCapacity(state, waiting, size, this.cornerOver(floor));

      const rowY = ticketTop(this.build, this.floorY(floor), this.floorHeight, size);

      waiting.slice(0, shown).forEach((passenger, index) => {
        visible.add(passenger.id);
        const chip = this.chipFor(passenger, size, alightFloor(state, passenger), this.build);
        this.place(chip, passenger, state, cursorX, rowY, index);
        cursorX += chipWidth(passenger, size, alightFloor(state, passenger)) + LAYOUT.passengerGap;
        chip.root.visible = true;

        this.paintMeter(chip, passenger, state, size);
        this.ringWalker(chip, passenger, state, size);

        const queued = queuedAnywhere.has(passenger.id);
        // In dispatch anyone waiting can be tapped, to send the lift to them; in
        // select, only who stands at an open door.
        const tappable = state.mode === 'dispatch' || (selectable && open.has(floor) && !queued);
        // Choosing who walks (ADR-0055): whoever cannot stands back.
        const back = queued || (this.stairsPick && !canTakeStairs(state, passenger));
        chip.root.alpha = (back ? 0.45 : 1) * this.waitingAlpha(chip, passenger, state);
        chip.hit.eventMode = tappable ? 'static' : 'none';
        chip.hit.cursor = tappable ? 'pointer' : 'default';

        if (chip.root.parent !== this.chipLayer) this.chipLayer.addChild(chip.root);
      });

      this.rowEnds.set(floor, cursorX);
      if (shown < waiting.length) {
        this.showCrowdPill(floor, cursorX, rowY, waiting.length - shown, size);
        shownCrowd.add(floor);
        this.rowEnds.set(floor, cursorX + CROWD_PILL_WIDTH + LAYOUT.passengerGap);
      }

      // Over the whole queue, shown or not: a group can be blocked from the back.
      const atDoor = open.get(floor);
      const blockedGroup = atDoor ? this.findBlockedGroup(atDoor, waiting, floor) : null;
      if (atDoor && blockedGroup !== null) {
        this.showOverflowLabel(atDoor, blockedGroup, floor);
        shownOverflow.add(floor);
      }
    }

    this.overflowLabels.forEach((label, floor) => {
      if (!shownOverflow.has(floor)) label.visible = false;
    });
    this.crowdPills.forEach((pill, floor) => {
      if (!shownCrowd.has(floor)) pill.root.visible = false;
    });

    this.startAlighting(state, size);
    this.moveLeavers(state, visible);
  }

  /** While the stairs are being picked (ADR-0055), a ring round whoever may walk. */
  private ringWalker(chip: Chip, passenger: Passenger, state: GameState, size: number): void {
    if (!this.stairsPick || !canTakeStairs(state, passenger)) return;
    const pulse = this.still ? 1 : 0.7 + 0.3 * Math.sin(this.clock / 160);
    chip.meter
      .roundRect(-5, -5, chip.width + 10, size + 10, chipRadius(size) + 5)
      .stroke({ width: 4, color: COLORS.target, alpha: pulse });
  }

  /**
   * A waiting person walks to their place: in from the corridor when they
   * arrive, up the queue as it moves, out of the car when they are changing
   * lifts. At their place they breathe, blink, look about, and — if impatient
   * and past half their patience — tap a foot.
   */
  private place(
    chip: Chip,
    passenger: Passenger,
    state: GameState,
    targetX: number,
    rowY: number,
    index: number,
  ): void {
    if (!chip.shown || chip.exit) {
      chip.exit = null;
      chip.bornMs = this.clock;
      chip.root.scale.set(1);
      chip.x = this.entryX(chip, passenger, state, targetX, index);
      chip.shown = true;
    }
    const step = this.still ? Number.POSITIVE_INFINITY : (WALK_SPEED * this.frameMs) / 1000;
    const heading = Math.sign(targetX - chip.x);
    chip.x = approach(chip.x, targetX, step);
    const walking = chip.x !== targetX;
    const stride = this.clock + chip.seed * 500;
    chip.root.x = chip.x;
    if (this.still) {
      chip.root.y = rowY;
      this.poseBody(chip, null, 0, 0);
    } else if (walking) {
      chip.root.y = rowY + walkBob(stride);
      this.poseBody(chip, stride, 0, heading);
    } else {
      this.stand(chip, passenger, state, rowY);
    }
    chip.lids.visible = !this.still && !walking && blinking(this.clock, chip.seed);
  }

  /**
   * Where someone comes into view. The opening queue is already standing
   * there; later arrivals come in from the far end of the floor, one behind
   * the other — off the train, on a platform — and someone changing lifts
   * steps out of the car.
   */
  private entryX(
    chip: Chip,
    passenger: Passenger,
    state: GameState,
    targetX: number,
    index: number,
  ): number {
    if (this.still || state.elapsedMs < 100) return targetX;
    if (passenger.boardedMs !== null) return this.shaftRight - chip.width * 0.5;
    const train =
      passenger.standingFloor === passenger.origin ? this.life.trainDoor(passenger.origin) : null;
    return train !== null ? train - chip.width / 2 : this.width + 8 + index * 14;
  }

  /** Waiting in place: breathing, looking about, an impatient foot tapping. */
  private stand(chip: Chip, passenger: Passenger, state: GameState, rowY: number): void {
    const patience = patienceRemaining(state, passenger);
    const urgency =
      patience !== null && passenger.patienceMs !== null
        ? Math.max(0, 0.5 - patience / passenger.patienceMs) * 2
        : 0;
    // Feet that show tap; otherwise the whole person does.
    const feet = chip.build !== null;
    chip.root.y = rowY + idleOffset(this.clock, chip.seed, feet ? 0 : urgency);
    const toe = feet ? toeLift(this.clock, chip.seed, urgency) : 0;
    const liftComing = this.liftFloors.includes(passenger.standingFloor);
    this.poseBody(chip, null, toe, gaze(this.clock, chip.seed, liftComing));
  }

  /**
   * The parts of a person that move on their own: legs swinging while they
   * walk (`stride` is their walking clock, null standing), a foot tapping,
   * eyes turning toward `look` — smoothly, over about a seventh of a second.
   */
  private poseBody(chip: Chip, stride: number | null, toe: number, look: number): void {
    const swing = stride === null || this.still ? 0 : legSwing(stride);
    const [left, right] = chip.legs;
    left.root.rotation = swing;
    right.root.rotation = -swing;
    left.shoe.y = -toe * TOE_UNITS * unit(LAYOUT.passengerSize);
    chip.gaze = this.still ? 0 : approach(chip.gaze, look, this.frameMs / 140);
    chip.eyes.x = chip.gaze * GAZE_UNITS * chip.eyeUnit;
    chip.lids.x = chip.eyes.x;
  }

  /**
   * A good stop, said (ADR-0037): several out at once, or a run of deliveries
   * with nobody lost. Words only — the score is the sim's, untouched.
   */
  private cheerDelivery(state: GameState, floor: number): void {
    const losses = state.score.abandoned + state.score.couriersLate;
    if (losses > this.lossesSeen) {
      this.lossesSeen = losses;
      this.streak = 0;
    }
    this.streak += 1;
    const recent = this.stopRuns.get(floor);
    const count = recent && this.clock - recent.at < STOP_WINDOW_MS ? recent.count + 1 : 1;
    this.stopRuns.set(floor, { count, at: this.clock });
    const x = Math.min(this.width - 80, this.shaftRight + LAYOUT.aisle + 70);
    const y = this.floorY(floor) - this.floorHeight * 0.15;
    const word = cheerFor(count) ?? streakCheer(this.streak);
    if (word) this.feedback.cheer(word, `stop-${floor}`, x, y, this.clock);
  }

  /** Riders who have just arrived at their floor step out of the car and walk off. */
  private startAlighting(state: GameState, size: number): void {
    for (const passenger of state.passengers) {
      if (passenger.state !== 'delivered' || this.alighted.has(passenger.id)) continue;
      this.alighted.add(passenger.id);
      if (passenger.deliveredMs === null) continue;
      if (state.elapsedMs - passenger.deliveredMs > 400) continue;
      this.scoreAt(passenger.destination, valueOfDelivery(passenger, passenger.deliveredMs).total);
      this.cheerDelivery(state, passenger.destination);
      if (this.still) continue;
      const floor = passenger.destination;
      const chip = this.chipFor(passenger, size, floor, this.build);
      const y = ticketTop(this.build, this.floorY(floor), this.floorHeight, size);
      const from = this.shaftRight - chip.width * 0.5;
      chip.exit = { kind: 'alight', startMs: this.clock, from, to: from + 80, y };
      // Behind the people still waiting: their tickets are what is being read.
      this.chipLayer.addChildAt(chip.root, 0);
      chip.meter.clear();
      chip.lids.visible = false;
      chip.root.alpha = 1;
      chip.root.visible = true;
      chip.shown = false;
    }
  }

  /**
   * Everyone no longer waiting on a floor: step into the car, walk off into
   * the room, or turn away and go — then gone. Without motion, simply gone.
   */
  private moveLeavers(state: GameState, visible: ReadonlySet<string>): void {
    for (const [id, chip] of this.chips) {
      if (visible.has(id)) continue;
      if (chip.shown && !chip.exit && !this.still) this.startLeaving(state, id, chip);
      chip.shown = false;
      this.stepLeaving(chip);
    }
  }

  /** Someone just stopped waiting: into the car, or away from it. */
  private startLeaving(state: GameState, id: string, chip: Chip): void {
    const passenger = state.passengers.find((p) => p.id === id);
    const kind: Exit | null =
      passenger?.state === 'riding' ? 'board' : passenger?.state === 'abandoned' ? 'storm' : null;
    if (!kind) return;
    const to = kind === 'board' ? this.shaftRight - chip.width * 0.6 : chip.x + 90;
    chip.exit = { kind, startMs: this.clock, from: chip.x, to, y: chip.root.y };
    this.chipLayer.addChildAt(chip.root, 0);
    chip.meter.clear();
    chip.lids.visible = false;
    if (kind === 'storm') this.paintPuff(chip);
  }

  /** One frame of someone leaving; hidden once they are gone. */
  private stepLeaving(chip: Chip): void {
    const exit = chip.exit;
    const t = exit ? (this.clock - exit.startMs) / EXIT_MS[exit.kind] : 1;
    if (!exit || t >= 1) {
      chip.exit = null;
      chip.root.visible = false;
      chip.root.scale.set(1);
      return;
    }
    const pose = exitPose(exit.kind, t, exit.from, exit.to);
    chip.root.visible = true;
    chip.root.x = pose.x;
    chip.root.y = exit.y + pose.dy;
    chip.root.alpha = pose.alpha;
    chip.root.scale.set(pose.scale);
    const stride = pose.walking ? this.clock + chip.seed * 500 : null;
    this.poseBody(chip, stride, 0, Math.sign(exit.to - exit.from));
  }

  /** A small cloud of frustration over someone who has given up. */
  private paintPuff(chip: Chip): void {
    const top = headTop(chip.build, LAYOUT.passengerSize) - 6;
    const x = chip.width / 2 + 8;
    for (const [dx, dy, r] of [
      [0, 0, 4],
      [5, -2, 3.5],
      [9, 1, 3],
    ] as const) {
      chip.meter
        .circle(x + dx, top + dy, r)
        .fill({ color: COLORS.cabin })
        .stroke({ width: 1, color: COLORS.ink });
    }
  }

  /**
   * How many of a floor's queue fit across the screen, keeping room for a "+N"
   * when they do not all fit.
   *
   * Up to fourteen people wait on one floor and a 375px phone has room for six
   * chips; the rest used to be drawn past the right edge, where nobody could see
   * them. Shown in queue order, which is the order the lift takes them in.
   */
  /** How much of a floor's row the corner's buttons cover: all of their width, or none. */
  private cornerOver(floor: number): number {
    const { width, height } = this.corner;
    return floor * this.floorHeight < height - LAYOUT.insetBottom ? width : 0;
  }

  private rowCapacity(
    state: GameState,
    waiting: readonly Passenger[],
    size: number,
    reserve: number,
  ): number {
    const room = this.width - (this.shaftRight + LAYOUT.aisle) - 4 - reserve;
    const widths = waiting.map(
      (p) => chipWidth(p, size, alightFloor(state, p)) + LAYOUT.passengerGap,
    );
    if (widths.reduce((sum, w) => sum + w, 0) - LAYOUT.passengerGap <= room) return waiting.length;
    let used = 0;
    let fits = 0;
    for (const w of widths) {
      if (used + w + CROWD_PILL_WIDTH > room) break;
      used += w;
      fits += 1;
    }
    return fits;
  }

  private showCrowdPill(floor: number, x: number, y: number, hidden: number, size: number): void {
    const pill = this.crowdPills[floor];
    if (!pill) return;
    pill.label.text = `+${hidden}`;
    pill.label.x = CROWD_PILL_WIDTH / 2;
    pill.label.y = size / 2;
    pill.bg.clear();
    pill.bg
      .roundRect(0, 0, CROWD_PILL_WIDTH, size, chipRadius(size))
      .fill({ color: COLORS.cabin })
      .stroke({ width: 1.5, color: COLORS.ink });
    pill.root.x = x;
    pill.root.y = y;
    pill.root.visible = true;
  }

  /**
   * The first still-waiting group the cabin cannot take whole, while there is
   * still a free seat for *someone*. A lone passenger not fitting is already
   * explained by the cabin reading FULL, so this only looks at groups.
   */
  private findBlockedGroup(
    state: GameState,
    waiting: readonly Passenger[],
    floor: number,
  ): Passenger | null {
    if (capacityOf(state) - state.elevator.load <= 0) return null;
    const seen = new Set<string>();
    for (const passenger of waiting) {
      if (passenger.groupId === null || seen.has(passenger.groupId)) continue;
      seen.add(passenger.groupId);
      if (!canBoard(state, passenger, floor)) return passenger;
    }
    return null;
  }

  private showOverflowLabel(state: GameState, passenger: Passenger, floor: number): void {
    const label = this.overflowLabels[floor];
    if (!label) return;
    const needed = queueableWith(state, passenger).reduce((sum, p) => sum + p.size, 0);
    const free = capacityOf(state) - state.elevator.load;
    label.text = `needs ${needed} · ${free} free`;
    label.visible = true;

    // Below the chip row rather than after it — a busy floor already fills the
    // width with chips, and a phone has no room to scroll sideways for a label
    // squeezed in at the end. Once tickets are held by people, below the row is
    // legs or the next floor, so it goes along the top of the floor instead:
    // clear wall above a whole person, hair on a compact one. A face is never
    // information.
    const rowX = this.shaftRight + LAYOUT.aisle;
    const centre = this.floorY(floor);
    label.y =
      this.build === null
        ? centre + LAYOUT.passengerSize / 2 + 9
        : centre - this.floorHeight / 2 + 9;
    if (rowX + label.width > this.width - 4) {
      label.anchor.set(1, 0.5);
      label.x = this.width - 4;
    } else {
      label.anchor.set(0, 0.5);
      label.x = rowX;
    }
  }

  private chipFor(passenger: Passenger, size: number, nextStop: number, build: Build | null): Chip {
    const key = this.chipKey(passenger, size, nextStop, build);
    const existing = this.chips.get(passenger.id);
    if (existing) {
      if (existing.lastKey !== key) this.paintChip(existing, passenger, size, nextStop, build);
      return existing;
    }

    const root = new Container();
    const hit = new Graphics();
    const figure = new Graphics();
    const hands = new Graphics();
    const plate = new Graphics();
    const meter = new Graphics();
    const lids = new Graphics();
    lids.visible = false;
    const eyes = new Graphics();
    const leg = (): Leg => {
      const part: Leg = { root: new Container(), limb: new Graphics(), shoe: new Graphics() };
      part.root.addChild(part.limb, part.shoe);
      return part;
    };
    const legs: readonly [Leg, Leg] = [leg(), leg()];
    const label = new Text({
      text: stopLabel(passenger, nextStop, this.ground),
      style: { ...CHIP_FONT, fontSize: 15, fill: COLORS.chipText },
    });
    label.anchor.set(0.5);
    // Legs under the coat, the person behind the ticket, their hands in front
    // of it, the label and the clocks over both. Hit target last, so nothing
    // can shadow it.
    root.addChild(legs[0].root, legs[1].root, figure, eyes, lids, plate, hands, label, meter, hit);
    // Names the archetype so the tree stays legible in automated checks.
    root.label = `passenger-${passenger.id}-${passenger.archetype}-to-${passenger.destination}`;
    // The chip is wider when it has to carry two numbers.
    hit.on('pointertap', (event) => {
      this.feedback.tap(event.global.x, event.global.y, this.clock);
      this.onPassengerTap(passenger.id, this.selectedCar);
    });

    const chip: Chip = {
      root,
      hit,
      figure,
      hands,
      plate,
      meter,
      label,
      lastKey: '',
      width: 0,
      build,
      look: lookOf(passenger),
      lids,
      eyes,
      eyeUnit: 0,
      gaze: 0,
      legs,
      x: 0,
      shown: false,
      bornMs: 0,
      seed: seedOf(passenger.id),
      exit: null,
    };
    this.paintChip(chip, passenger, size, nextStop, build);
    this.chips.set(passenger.id, chip);
    this.chipLayer.addChild(root);
    return chip;
  }

  private paintChip(
    chip: Chip,
    passenger: Passenger,
    size: number,
    nextStop: number,
    build: Build | null,
  ): void {
    const width = chipWidth(passenger, size, nextStop);
    chip.width = width;
    chip.build = build;
    paintFigure(chip.figure, chip.hands, passenger, chip.look, build, width, size);
    chip.legs.forEach((leg, which) => {
      paintLeg(leg.root, leg.limb, leg.shoe, passenger, build, width, size, which as 0 | 1);
    });
    chip.eyeUnit = paintEyes(chip.eyes, passenger, build, width, size);
    paintLids(chip.lids, passenger, chip.look, build, width, size);

    // Padded beyond the visible chip: 30px is a small target for a thumb.
    const pad = 5;
    chip.hit.clear();
    chip.hit
      .rect(-pad, -pad, width + pad * 2, size + pad * 2)
      .fill({ color: 0xffffff, alpha: 0.001 });
    const radius = chipRadius(size);
    chip.plate.clear();
    chip.plate
      .roundRect(0, 0, width, size, radius)
      .fill({ color: floorColor(nextStop) })
      .stroke({ width: 1.5, color: COLORS.ink });

    // One visual channel per trait, drawn only when the trait is present. The
    // roadmap's UI-overload risk is real: a player who is reading icons is not
    // watching traffic.
    if (passenger.vip) {
      chip.plate
        .roundRect(-3, -3, width + 6, size + 6, radius + 3)
        .stroke({ width: 2, color: COLORS.vip });
    }
    if (passenger.size > 1) {
      // A second body inside the plate, so "two of you" reads without a legend.
      chip.plate
        .roundRect(width - size * 0.62, size * 0.22, size * 0.44, size * 0.56, 4)
        .fill({ color: COLORS.chipText, alpha: 0.3 });
    }
    if (passenger.groupId !== null) {
      // Left edge, not the bottom: the bottom belongs to the patience meter, and
      // a passenger with both traits would otherwise show two bars in one place.
      // Inset so it stays inside the rounded corners.
      chip.plate.roundRect(3, size * 0.18, 3, size * 0.64, 1.5).fill({ color: COLORS.groupStripe });
    }

    // Scaled with the chip: a cabin seat is smaller than a waiting chip, and a
    // transfer label carries two numbers.
    const base = passenger.errands.length > 0 ? 12 : nextStop === passenger.destination ? 15 : 13;
    chip.label.text = stopLabel(passenger, nextStop, this.ground);
    chip.label.style.fontSize = Math.round(base * Math.min(1, size / LAYOUT.passengerSize));
    chip.label.x = passenger.size > 1 ? (width - size * 0.62) / 2 : width / 2;
    chip.label.y = size / 2;
    chip.lastKey = this.chipKey(passenger, size, nextStop, build);
  }

  private chipKey(
    passenger: Passenger,
    size: number,
    nextStop: number,
    build: Build | null,
  ): string {
    return [
      build ? `${build.stretch.toFixed(3)}/${build.crown.toFixed(2)}` : 'ticket',
      size,
      passenger.destination,
      nextStop,
      passenger.size,
      passenger.vip ? 'v' : '',
      passenger.groupId ?? '',
      passenger.errands.join('.'),
    ].join(':');
  }

  /**
   * The clocks. Patience drains along the bottom edge, a courier deadline along
   * the top — both shortening bars rather than numbers, because a bar can be
   * read out of the corner of an eye while the car is moving.
   */
  private paintMeter(chip: Chip, passenger: Passenger, state: GameState, size: number): void {
    chip.meter.clear();
    // Inset so the bars stay inside the rounded corners.
    const inset = Math.round(size * 0.2);
    const span = chip.width - inset * 2;

    // Asked of the sim rather than worked out here: patience restarts after a
    // transfer, and a bar computed from arrival time showed a rider who had just
    // changed lifts as already out of patience.
    const patience = patienceRemaining(state, passenger);
    if (patience !== null && passenger.patienceMs !== null) {
      const fraction = patience / passenger.patienceMs;
      this.drawBar(chip.meter, inset, size - 5, span, fraction, patienceColour(fraction));
      if (fraction < 0.5) paintSweat(chip.meter, passenger, chip.build, chip.width, size);
    }
    this.paintMarks(chip, passenger, state);

    const deadline = deadlineRemaining(state, passenger);
    if (deadline !== null && passenger.deadlineMs !== null) {
      const fraction = deadlineFraction(state, passenger) ?? 1;
      const colour = fraction > 0.3 ? COLORS.courier : COLORS.patienceOut;
      this.drawBar(chip.meter, inset, 2, span, fraction, colour);
    }
  }

  /** The marks above the ticket: a warning, a crew's siren, a mark to go first. */
  private paintMarks(chip: Chip, passenger: Passenger, state: GameState): void {
    // No one left to lose, and this one's clock is past half: losing them ends the run.
    // An emergency's loss ends the run whatever there is to spare (ADR-0029).
    if ((this.spare === 0 || passenger.critical) && runningOut(state, passenger)) {
      paintWarning(chip.meter, chip.width + 1, -2, this.clock);
    }
    // A crew's siren at the ticket's corner, clear of their head (ADR-0029).
    if (passenger.critical) {
      const kind = crewOf(passenger) === 'police' ? 'police' : 'ambulance';
      paintSiren(chip.meter, 2, -1, kind, this.clock, this.still);
    }
  }

  private drawBar(
    g: Graphics,
    x: number,
    y: number,
    span: number,
    fraction: number,
    colour: number,
  ): void {
    g.roundRect(x, y, span, 3, 1.5).fill({ color: COLORS.meterTrack, alpha: 0.5 });
    if (fraction > 0) g.roundRect(x, y, span * fraction, 3, 1.5).fill({ color: colour });
  }

  destroy(): void {
    this.root.destroy({ children: true });
    this.chips.clear();
  }
}
